import { adminService } from "../../back-office/services/admin";
import { t } from "i18next";
import { ExportDataContents } from "../../front-office/model/export-data-contents";
import { ExportDataItem } from "../../front-office/model/export-data-item";
import { excelDataExporter } from "../component/list/import-export-list/excel-data-exporter";
import { editorHelper } from "../../back-office/components/operations/editor-helper";
import { notificationModel } from "../component/notifications/notification-model";
import { dialogModel } from "../component/dialog/dialog-model";
import { ExcelIncorrectOperationProductDataDecorator } from "../component/list/import-export-list/excel-incorrect-operation-product-data-decorator";
import * as Excel from "exceljs";
import { ExportDataWorksheet } from "../../front-office/model/export-data-worksheet";
import { ProductExportReadmeDecorator } from "../component/list/import-export-list/product-export-readme-decorator";

export class OperationProductsEditorModel {
    private readonly _operationCode: string;

    constructor(operationCode: string) {
        this._operationCode = operationCode;
    }

    public get operationCode(): string {
        return this._operationCode;
    }

    public async downloadTemplateWithExportedData(fileName?: string) {
        const exportDataContents = ExportDataContents.exportMultiWorksheet(
            fileName ?? "products_with_mono_parcel_management_template",
            [await this.buildWorksheetWithProductsData(), this.buildWorksheetWithReadme()]);

        const validateRow = (excelRow: Excel.Row) => {
            const headerColumns = this.buildHeaderExportDataItem().columns;
            const lengthColumnValue = excelRow.getCell(headerColumns.indexOf(this.lengthColumnName) + 1).value?.toString() ?? "";
            const widthColumnValue = excelRow.getCell(headerColumns.indexOf(this.widthColumnName) + 1).value?.toString() ?? "";
            const heightColumnValue = excelRow.getCell(headerColumns.indexOf(this.heightColumnName) + 1).value?.toString() ?? "";
            const weightColumnValue = excelRow.getCell(headerColumns.indexOf(this.weightColumnName) + 1).value?.toString() ?? "";
            const barcodesColumnValue = excelRow.getCell(headerColumns.indexOf(this.barcodesColumnName) + 1).value?.toString().trim() ?? "";

            const invalidColumns: string[] = [];

            if ((lengthColumnValue === "1" || lengthColumnValue === "0" || lengthColumnValue === "")
                && (widthColumnValue === "1" || widthColumnValue === "0" || widthColumnValue === "")
                && (heightColumnValue === "1" || heightColumnValue === "0" || heightColumnValue === "")
                && (weightColumnValue === "499" || weightColumnValue === "0" || weightColumnValue === "")
            ) {
                invalidColumns.push(...[this.lengthColumnName, this.widthColumnName, this.heightColumnName, this.weightColumnName]);
            }

            if (barcodesColumnValue === "") {
                invalidColumns.push(this.barcodesColumnName);
            }

            return invalidColumns.map(columnName => headerColumns.indexOf(columnName));
        };

        excelDataExporter.export(exportDataContents, [
            new ExcelIncorrectOperationProductDataDecorator(excelRow => validateRow(excelRow)),
            new ProductExportReadmeDecorator(t("model.operationProductsFormData.readme.header"), [
                t("model.operationProductsFormData.readme.purpose.header"),
                t("model.operationProductsFormData.readme.monoParcelTag.header"),
                t("model.operationProductsFormData.readme.columnsOverview.header"),
                t("model.operationProductsFormData.readme.columnsOverview.subheader1"),
                t("model.operationProductsFormData.readme.columnsOverview.subheader2"),
                t("model.operationProductsFormData.readme.bundleTypes.header"),
                t("model.operationProductsFormData.readme.bundleTypes.subheader1"),
                t("model.operationProductsFormData.readme.bundleTypes.subheader2"),
                t("model.operationProductsFormData.readme.bundleTypes.subheader3"),
            ], [
                t("model.operationProductsFormData.readme.bundleTypes.list.lotDimensions"),
                t("model.operationProductsFormData.readme.bundleTypes.list.kitDimensions"),
            ]),
        ]);
    }

    public async uploadOperationProducts(file: File) {
        const data = await editorHelper.readUploadedFile(file,
            "product.operationProduct.processingUploadedFile",
            "product.operationProduct.uploadedFileFailed");

        if (data.length === 0) {
            notificationModel.addWarningMessage("product.operationProduct.emptyUpload");
            return;
        }

        dialogModel.confirmBox(
            t("components.form.action.submitConfirmTitle"),
            t("product.operationProduct.saveConfirmMany", {count: data.length, operationCode: this._operationCode}),
            async () => await this.executeComponentsUpdate(data),
        );
    }

    private async executeComponentsUpdate(data: Array<Map<string, string>>) {
        if (!this.validateImportFile(data)) {
            throw new Error(t("product.operationProduct.uploadedFileIncorrect"));
        }

        const duplicates = this.validateImportData(data);
        if (duplicates.length !== 0) {
            notificationModel.addErrorMessage("product.operationProduct.uploadedDataIncorrect", { duplicatedIds: duplicates });
            return;
        }

        await adminService.updateOperationProductsMonoParcel(this._operationCode, data.map(row => {
            return {
                productId: Number(row.get(this.productIdColumnName) ?? 0),
                monoParcel: Boolean(Number(row.get(this.monoParcelColumnName) ?? 0)),
            };
        }));

        notificationModel.addSuccessMessage("product.operationProduct.savedSuccessMany", {
            count: data.length,
            operationCode: this._operationCode,
        });
    }

    private validateImportFile(data: Array<Map<string, string>>) {
        return data[0].get(this.productIdColumnName) !== undefined &&
            data[0].get(this.productNameColumnName) !== undefined &&
            data[0].get(this.itemIdColumnName) !== undefined &&
            data[0].get(this.supplierReferenceColumnName) !== undefined &&
            data[0].get(this.barcodesColumnName) !== undefined &&
            data[0].get(this.widthColumnName) !== undefined &&
            data[0].get(this.heightColumnName) !== undefined &&
            data[0].get(this.lengthColumnName) !== undefined &&
            data[0].get(this.weightColumnName) !== undefined &&
            data[0].get(this.axRefGroupTypeColumnName) !== undefined &&
            data[0].get(this.monoParcelColumnName) !== undefined;
    }

    private validateImportData(data: Array<Map<string, string>>) {
        const productsIds = data.map(row => Number(row.get(this.productIdColumnName) ?? 0));
        return productsIds.filter((item, index) => productsIds.indexOf(item) !== index);
    }

    private buildHeaderExportDataItem() {
        return new ExportDataItem([
            this.itemIdColumnName,
            this.productIdColumnName,
            this.barcodesColumnName,
            this.supplierReferenceColumnName,
            this.productNameColumnName,
            this.lengthColumnName,
            this.widthColumnName,
            this.heightColumnName,
            this.dimensionsColumnsName,
            this.weightColumnName,
            this.axRefGroupTypeColumnName,
            this.monoParcelColumnName,
        ]);
    }

    private async buildContentsExportDataItems() {
        return (await adminService.getOperationProducts(this._operationCode))
            .map(p => new ExportDataItem([
                p.itemId,
                p.productId,
                p.barcodes.join(", "),
                p.supplierReference,
                p.productName,
                p.length,
                p.width,
                p.height,
                p.length + p.width + p.height,
                p.weight,
                p.axRefGroupType,
                +p.monoParcel,
            ]));
    }

    private async buildWorksheetWithProductsData() {
        return new ExportDataWorksheet(this.buildHeaderExportDataItem(), await this.buildContentsExportDataItems());
    }

    private buildWorksheetWithReadme() {
        return new ExportDataWorksheet(
            new ExportDataItem([t("model.operationProductsFormData.readme.header")]),
            [
                new ExportDataItem(Array(10).fill("")),
                new ExportDataItem([t("model.operationProductsFormData.readme.purpose.header")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.purpose.line1")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.purpose.line2")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.purpose.line3")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.purpose.line4")]),
                new ExportDataItem([""]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.header")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line1")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line2")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line3")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line4")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line5")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line6")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line7")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.monoParcelTag.line8")]),
                new ExportDataItem([""]),
                new ExportDataItem([t("model.operationProductsFormData.readme.columnsOverview.header")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.columnsOverview.subheader1"),
                    t("model.operationProductsFormData.readme.columnsOverview.subheader2")]),
                new ExportDataItem([t("model.operationProductsFormData.itemId"),
                    t("model.operationProductsFormData.readme.columnsOverview.columnIdentifierDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.productId"),
                    t("model.operationProductsFormData.readme.columnsOverview.productIdDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.barcodes"),
                    t("model.operationProductsFormData.readme.columnsOverview.barcodesDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.supplierReference"),
                    t("model.operationProductsFormData.readme.columnsOverview.supplierReferenceDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.productName"),
                    t("model.operationProductsFormData.readme.columnsOverview.productNameDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.packageDimensionsAll"),
                    t("model.operationProductsFormData.readme.columnsOverview.packageDimensionsAllDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.dimensions"),
                    t("model.operationProductsFormData.readme.columnsOverview.dimensionsDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.weight"),
                    t("model.operationProductsFormData.readme.columnsOverview.weightDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.axRefGroupType"),
                    t("model.operationProductsFormData.readme.columnsOverview.axRefGroupTypeDescription")]),
                new ExportDataItem([t("model.operationProductsFormData.monoParcel"),
                    t("model.operationProductsFormData.readme.columnsOverview.monoParcelDescription")]),
                new ExportDataItem([""]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.header")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.subheader1"),
                    t("model.operationProductsFormData.readme.bundleTypes.subheader2"),
                    t("model.operationProductsFormData.readme.bundleTypes.subheader3")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.list.lot"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.lotDescription"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.lotDimensions")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.list.comp"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.compDescription"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.compDimensions")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.list.kit"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.kitDescription"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.kitDimensions")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.list.par"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.parDescription"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.parDimensions")]),
                new ExportDataItem([t("model.operationProductsFormData.readme.bundleTypes.list.pack"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.packDescription"),
                    t("model.operationProductsFormData.readme.bundleTypes.list.packDimensions")]),
            ],
            "Readme",
        );
    }

    private get productIdColumnName() {
        return t("model.operationProductsFormData.productId");
    }

    private get itemIdColumnName() {
        return t("model.operationProductsFormData.itemId");
    }

    private get productNameColumnName() {
        return t("model.operationProductsFormData.productName");
    }

    private get supplierReferenceColumnName() {
        return t("model.operationProductsFormData.supplierReference");
    }

    private get barcodesColumnName() {
        return t("model.operationProductsFormData.barcodes");
    }

    private get widthColumnName() {
        return t("model.operationProductsFormData.width");
    }

    private get heightColumnName() {
        return t("model.operationProductsFormData.height");
    }

    private get lengthColumnName() {
        return t("model.operationProductsFormData.length");
    }

    private get weightColumnName() {
        return t("model.operationProductsFormData.weight");
    }

    private get dimensionsColumnsName() {
        return t("model.operationProductsFormData.dimensions");
    }

    private get axRefGroupTypeColumnName() {
        return t("model.operationProductsFormData.axRefGroupType");
    }

    private get monoParcelColumnName() {
        return t("model.operationProductsFormData.monoParcel");
    }
}
